/**
 * DemoDirectoryManagerPage (`/admin/demos`)
 *
 * Super-simple admin CRUD for the public demo directory. Each entry
 * has a business name, auto-generated slug, and a 5-digit access code.
 * The operator reads the code aloud to the client at unlock time.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";
import DemoSavingsLink from "@/components/admin/DemoSavingsLink";
import TemplateDemoCreator from "@/components/admin/TemplateDemoCreator";
import ShareLinksPanel from "@/components/admin/ShareLinksPanel";
import DemoTemplateSettings, { isTemplated } from "@/components/admin/DemoTemplateSettings";
import { Building2, Plus, Save, Trash2, X, ArrowLeft, KeyRound, Copy, ExternalLink, Rocket } from "lucide-react";

const emptyDraft = { business_name: "", access_code: "", notes: "" };

export default function DemoDirectoryManagerPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {id, business_name, access_code, notes}

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/demo-directory");
      setItems(data?.items || []);
    } catch {
      toast.error("Could not load demo directory");
    } finally {
      setLoading(false);
    }
  };

  const [briefs, setBriefs] = useState([]);
  useEffect(() => {
    reload();
    apiClient.get("/admin/briefs").then(({ data }) => setBriefs(data?.items || [])).catch(() => {});
  }, []);

  const sanitizeCode = (v) => v.replace(/\D/g, "").slice(0, 5);

  const create = async () => {
    if (!draft.business_name.trim()) return toast.error("Business name is required");
    if (draft.access_code.length !== 5) return toast.error("Code must be exactly 5 digits");
    setSaving(true);
    try {
      await apiClient.post("/admin/demo-directory", draft);
      toast.success("Demo added");
      setDraft(emptyDraft);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add demo");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editing.business_name.trim()) return toast.error("Business name is required");
    if (editing.access_code.length !== 5) return toast.error("Code must be exactly 5 digits");
    setSaving(true);
    try {
      await apiClient.patch(`/admin/demo-directory/${editing.id}`, {
        business_name: editing.business_name,
        access_code: editing.access_code,
        notes: editing.notes || "",
      });
      toast.success("Demo updated");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}" demo entry? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/admin/demo-directory/${id}`);
      toast.success("Demo removed");
      reload();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const copy = (text, label) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {}
  };

  return (
    <section
      data-testid="demo-directory-manager"
      className="min-h-screen bg-[#0A192F] text-slate-100 py-10 px-6 md:px-10"
    >
      <div className="max-w-6xl mx-auto">
        <Link
          to="/os"
          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-brand-cyan font-mono text-[11px] tracking-[0.18em] uppercase mb-6 transition"
          data-testid="demo-mgr-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to OS
        </Link>
        <Link to="/admin/pilot-requests" data-testid="demo-mgr-pilot-requests"
          className="float-right inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 px-4 py-1.5 text-[#F4D88A] hover:bg-[#D4AF37]/10 font-mono text-[11px] tracking-[0.18em] uppercase transition-colors">
          <Rocket className="w-3.5 h-3.5" /> Pilot Requests
        </Link>

        <div className="flex items-center gap-2 text-brand-cyan font-mono text-[11px] tracking-[0.24em] uppercase mb-2">
          <KeyRound className="w-3.5 h-3.5" /> // DEMO DIRECTORY MANAGER
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">
          Client demos & access codes
        </h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          Add a business, give it a 5-digit code. The public directory lists business names only
          — codes are read aloud at the unlock screen.
        </p>

        <TemplateDemoCreator briefs={briefs} onCreated={reload} />
        <ShareLinksPanel demos={items} />

        {/* Add row */}
        <div
          data-testid="demo-mgr-add-card"
          className="mt-8 rounded-2xl border border-white/10 bg-navy-900/60 backdrop-blur-md p-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_1fr_auto] gap-3">
            <input
              value={draft.business_name}
              onChange={(e) => setDraft({ ...draft, business_name: e.target.value })}
              placeholder="Business name"
              data-testid="demo-mgr-input-name"
              className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-white font-mono text-sm"
            />
            <input
              value={draft.access_code}
              onChange={(e) => setDraft({ ...draft, access_code: sanitizeCode(e.target.value) })}
              placeholder="5-digit code"
              inputMode="numeric"
              maxLength={5}
              data-testid="demo-mgr-input-code"
              className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-white font-mono text-sm tracking-[0.3em] text-center"
            />
            <input
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="Internal notes (optional)"
              data-testid="demo-mgr-input-notes"
              className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-slate-300 font-mono text-sm"
            />
            <button
              type="button"
              onClick={create}
              disabled={saving}
              data-testid="demo-mgr-add-btn"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-mono text-xs font-semibold tracking-[0.14em] uppercase px-4 py-2 transition disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> Add demo
            </button>
          </div>
        </div>

        {/* List */}
        <div className="mt-8">
          {loading && (
            <div className="text-slate-500 font-mono text-sm" data-testid="demo-mgr-loading">
              Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div
              data-testid="demo-mgr-empty"
              className="rounded-xl border border-white/10 bg-navy-900/60 p-8 text-center text-slate-400"
            >
              No demos yet. Add one above.
            </div>
          )}
          {!loading && items.length > 0 && (
            <ul data-testid="demo-mgr-list" className="space-y-3">
              {items.map((it) => {
                const isEditing = editing?.id === it.id;
                return (
                  <li
                    key={it.id}
                    data-testid={`demo-mgr-row-${it.slug}`}
                    className="rounded-xl border border-white/10 bg-navy-900/60 backdrop-blur-md p-4"
                  >
                    {!isEditing ? (
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-tr from-brand-blue via-brand-cyan to-brand-emerald flex items-center justify-center shrink-0">
                          <Building2 className="w-5 h-5 text-navy-950" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-white flex items-center gap-2">
                            {it.business_name}
                            {isTemplated(it) && <span className="px-2 py-0.5 rounded-full border border-emerald-400/40 text-emerald-300 font-mono text-[9px] tracking-[0.16em] uppercase" data-testid={`demo-mgr-template-badge-${it.slug}`}>{it.template === "exchange" ? "Exchange engine" : "Template engine"}</span>}
                          </div>
                          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mt-1 flex items-center gap-3">
                            <span>slug: {it.slug}</span>
                            <button
                              onClick={() => copy(`${window.location.origin}/demos/${it.slug}`, "URL")}
                              className="inline-flex items-center gap-1 text-slate-400 hover:text-brand-cyan transition"
                              data-testid={`demo-mgr-copy-url-${it.slug}`}
                            >
                              <Copy className="w-3 h-3" /> copy URL
                            </button>
                            <Link
                              to={`/demos/${it.slug}/unlock`}
                              target="_blank"
                              className="inline-flex items-center gap-1 text-slate-400 hover:text-brand-cyan transition"
                              data-testid={`demo-mgr-open-${it.slug}`}
                            >
                              <ExternalLink className="w-3 h-3" /> open
                            </Link>
                          </div>
                          {it.notes && <div className="text-slate-400 text-xs mt-1">{it.notes}</div>}
                        </div>
                        <div className="font-mono text-xl tracking-[0.3em] text-brand-cyan px-3 py-1 rounded-lg bg-brand-cyan/10 border border-brand-cyan/30">
                          {it.access_code}
                        </div>
                        <button
                          onClick={() => setEditing({ id: it.id, business_name: it.business_name, access_code: it.access_code, notes: it.notes || "" })}
                          data-testid={`demo-mgr-edit-${it.slug}`}
                          className="inline-flex items-center gap-1 text-slate-300 hover:text-white font-mono text-xs tracking-[0.14em] uppercase px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/30 transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => remove(it.id, it.business_name)}
                          data-testid={`demo-mgr-delete-${it.slug}`}
                          className="inline-flex items-center gap-1 text-rose-400 hover:text-rose-300 font-mono text-xs tracking-[0.14em] uppercase px-3 py-1.5 rounded-lg border border-rose-500/30 hover:border-rose-500/60 transition"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_1fr_auto_auto] gap-3">
                        <input
                          value={editing.business_name}
                          onChange={(e) => setEditing({ ...editing, business_name: e.target.value })}
                          data-testid={`demo-mgr-edit-name-${it.slug}`}
                          className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-white font-mono text-sm"
                        />
                        <input
                          value={editing.access_code}
                          onChange={(e) => setEditing({ ...editing, access_code: sanitizeCode(e.target.value) })}
                          inputMode="numeric"
                          maxLength={5}
                          data-testid={`demo-mgr-edit-code-${it.slug}`}
                          className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-white font-mono text-sm tracking-[0.3em] text-center"
                        />
                        <input
                          value={editing.notes}
                          onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                          data-testid={`demo-mgr-edit-notes-${it.slug}`}
                          className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-slate-300 font-mono text-sm"
                        />
                        <button
                          onClick={saveEdit}
                          disabled={saving}
                          data-testid={`demo-mgr-save-${it.slug}`}
                          className="inline-flex items-center gap-1 bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-mono text-xs font-semibold tracking-[0.14em] uppercase px-3 py-2 rounded-lg transition disabled:opacity-50"
                        >
                          <Save className="w-3.5 h-3.5" /> Save
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          data-testid={`demo-mgr-cancel-${it.slug}`}
                          className="inline-flex items-center gap-1 text-slate-300 hover:text-white font-mono text-xs tracking-[0.14em] uppercase px-3 py-2 rounded-lg border border-white/10 hover:border-white/30 transition"
                        >
                          <X className="w-3.5 h-3.5" /> Cancel
                        </button>
                      </div>
                    )}
                    <DemoSavingsLink demo={it} briefs={briefs} onSaved={reload} />
                    {isTemplated(it) && <DemoTemplateSettings demo={it} onSaved={reload} />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
